import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import ZoomableImage from '../components/ZoomableImage';
import SEO from '../components/SEO';
import { 
  Sparkles, 
  Check, 
  Upload, 
  X, 
  CheckCircle2, 
  AlertCircle, 
  Loader2, 
  Image as ImageIcon,
  Filter,
  Info,
  ArrowRight
} from 'lucide-react';

export default function FarverOgDesign() {
  const [activeFilter, setActiveFilter] = useState('all');
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [messageText, setMessageText] = useState('');
  const fileInputRef = useRef(null);

  // File constraints
  const MAX_FILES = 5;
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
  const MAX_TOTAL_SIZE = 20 * 1024 * 1024; // 20 MB
  const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (selectedFiles.length + files.length > MAX_FILES) {
      alert(`Du kan maksimalt uploade ${MAX_FILES} billeder.`);
      return;
    }

    const validFiles = [];
    let currentTotalSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);

    for (const file of files) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        alert(`Filtypen "${file.name}" er ikke tilladt. Kun PNG, JPEG og WEBP understøttes.`);
        continue;
      }

      if (file.size > MAX_FILE_SIZE) {
        alert(`Billedet "${file.name}" er for stort. Maksimal størrelse er 10 MB.`);
        continue;
      }

      if (currentTotalSize + file.size > MAX_TOTAL_SIZE) {
        alert(`Den samlede filstørrelse overstiger grænsen på 20 MB.`);
        break;
      }

      currentTotalSize += file.size;
      validFiles.push(file);
    }

    setSelectedFiles((prev) => [...prev, ...validFiles]);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Append files
    formData.delete('billeder');
    selectedFiles.forEach((file) => {
      formData.append('billeder', file);
    });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (response.ok && result.status === 'ok') {
        setStatus('success');
        setSelectedFiles([]);
        form.reset();
      } else {
        setStatus('error');
        setErrorMessage(result.message || 'Der skete en fejl under afsendelsen. Prøv venligst igen.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus('error');
      setErrorMessage('Forbindelsesfejl. Tjek din internetforbindelse og prøv igen.');
    }
  };

  const designCategories = [
    { title: 'Lyse stenblandinger', desc: 'Giver et let, skandinavisk og åbent udtryk, der reflekterer lyset smukt.' },
    { title: 'Mørke stenblandinger', desc: 'Skaber en eksklusiv, dramatisk og moderne kontrast til lyse husfacader.' },
    { title: 'Naturlige nuancer', desc: 'Klassiske jordfarver, der falder harmonisk ind i havens grønne omgivelser.' },
    { title: 'Moderne grå toner', desc: 'Minimalistiske og stilrene nuancer, der passer perfekt til nybyggeri.' },
    { title: 'Varme sandfarver', desc: 'Sydlandsk stemning med bløde, varme og indbydende farvetoner.' },
    { title: 'Eksklusive kontraster', desc: 'Sammensætninger med markante farvespil, der skaber liv og dynamik.' },
    { title: 'Klassiske & tidløse kombinationer', desc: 'Sikre valg, der bevarer deres elegance på tværs af skiftende trends.' }
  ];

  const vubaColors = [
    // Varme & Rødlige
    {
      name: 'Tyrian',
      category: 'warm-red',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/tyrian.webp'
    },
    {
      name: 'Tawny',
      category: 'warm-red',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/tawny.webp'
    },
    {
      name: 'Saxon',
      category: 'warm-red',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/saxon.webp'
    },
    {
      name: 'Red Marble',
      category: 'warm-red',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/red-marble.webp'
    },
    {
      name: 'Red Granit',
      category: 'warm-red',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/red-granit.webp'
    },
    // Unikke & Kontraster
    {
      name: 'Pantagonia',
      category: 'unique-contrast',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/pantagonia.webp'
    },
    {
      name: 'Irish Bronze',
      category: 'unique-contrast',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/irish-bronze.webp'
    },
    {
      name: 'Ipanema Beach',
      category: 'unique-contrast',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/ipanema-beach.webp'
    },
    {
      name: 'Coshel Quartz',
      category: 'unique-contrast',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/coshel-quartz.webp'
    },
    {
      name: 'Ballagio',
      category: 'unique-contrast',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/ballagio.webp'
    },
    // Mørke & Grå
    {
      name: 'Posidon',
      category: 'dark-grey',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/posidon.webp'
    },
    {
      name: 'Nero Marble',
      category: 'dark-grey',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/nero-marble.webp'
    },
    {
      name: 'Gunmetal Quartz',
      category: 'dark-grey',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/gunmetal-quartz.webp'
    },
    {
      name: 'Galahad Grey',
      category: 'dark-grey',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/galahad-grey.webp'
    },
    {
      name: 'Black Bassalt',
      category: 'dark-grey',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/black-bassalt.webp'
    },
    // Lyse & Sandfarvede
    {
      name: 'Tuscan',
      category: 'light-sand',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/tuscan.webp'
    },
    {
      name: 'Tortora Marble',
      category: 'light-sand',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/tortora-marble.webp'
    },
    {
      name: 'St. Ives',
      category: 'light-sand',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/st-ives.webp'
    },
    {
      name: 'Rosa Marble',
      category: 'light-sand',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/rosa-marble.webp'
    },
    {
      name: 'Paloma',
      category: 'light-sand',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/paloma.webp'
    },
    {
      name: 'Necker Island',
      category: 'light-sand',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/necker-island.webp'
    },
    {
      name: 'Ivory Marble',
      category: 'light-sand',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/ivory-marble.webp'
    },
    {
      name: 'Fall Quartz',
      category: 'light-sand',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/fall-quartz.webp'
    },
    {
      name: 'Athena Quartz',
      category: 'light-sand',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/athena-quartz.webp'
    },
    {
      name: 'Alaska Thundra',
      category: 'light-sand',
      img: 'https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/alaska-thundra.webp'
    }
  ];

  return (
    <div className="pt-24 md:pt-40 xl:pt-28 pb-16">
      <SEO
        title="Farver & Design | Vælg Mellem Eksklusive Natursten | Stenbelægning.dk"
        description="Se vores store galleri af farver og stentyper til helstøbt stenbelægning. Find det helt rigtige farvespil til din indkørsel, terrasse, trappe eller havesti."
      />
      {/* Hero Header */}
      <section className="bg-stone-50 py-16 border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center max-w-4xl">
          <span className="text-xs font-bold tracking-widest uppercase text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">
            Eksklusivt Farveudvalg
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-stone-900 tracking-tight mt-4">
            Farver og Design
          </h1>
          <p className="text-lg text-stone-600 mt-4 leading-relaxed">
            Udforsk hele den originale <strong>Vuba Europe-kollektion</strong> med 25 unikke farver og stenblandinger. Alt fra lyse skandinaviske sandtoner til dybe moderne kontraster.
          </p>
        </div>
      </section>

      {/* Main Colors Showcase */}
      <section id="farver" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Filter Controls */}
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {[
              { id: 'all', label: 'Vis alle (25)' },
              { id: 'light-sand', label: 'Lyse & Sandfarvede' },
              { id: 'dark-grey', label: 'Mørke & Grå' },
              { id: 'warm-red', label: 'Varme & Rødlige' },
              { id: 'unique-contrast', label: 'Unikke & Kontraster' }
            ].map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`px-4 py-2 rounded-full text-xs font-bold tracking-wide uppercase transition-all duration-200 flex items-center gap-1.5 border cursor-pointer ${
                  activeFilter === filter.id
                    ? 'bg-emerald-600 border-emerald-600 text-white shadow-sm'
                    : 'bg-white border-stone-200 text-stone-600 hover:border-stone-300 hover:text-stone-900'
                }`}
              >
                {filter.id === 'all' && <Filter className="h-3.5 w-3.5" />}
                {filter.label}
              </button>
            ))}
          </div>

          {/* Grid med alle farver */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {vubaColors
              .filter(color => activeFilter === 'all' || color.category === activeFilter)
              .map((color, idx) => {
                const hasImage = !!color.img;
                return (
                  <div 
                    key={idx} 
                    className="bg-white rounded-2xl border border-stone-200 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 flex flex-col group"
                  >
                    {hasImage ? (
                      <ZoomableImage
                        src={color.img}
                        alt={color.name}
                        className="aspect-square bg-stone-100 shrink-0"
                        zoomScale={3.2}
                        optimizedWidth={800}
                      />
                    ) : (
                      <div className="aspect-square relative overflow-hidden bg-gradient-to-br from-stone-50 to-stone-100 shrink-0 flex flex-col items-center justify-center p-6 text-center border-b border-stone-100">
                        <div className="w-12 h-12 rounded-full bg-stone-200/50 flex items-center justify-center text-stone-500 mb-3 group-hover:scale-110 transition-transform duration-300">
                          <ImageIcon className="h-6 w-6" />
                        </div>
                        <span className="text-[10px] font-bold tracking-wider uppercase text-stone-500 bg-stone-200/40 px-2 py-1 rounded-full">
                          Fysisk prøve
                        </span>
                        <p className="text-[11px] text-stone-500 mt-2 leading-relaxed">
                          Fysisk stenprøve er tilgængelig i vores showroom
                        </p>
                      </div>
                    )}

                    <div className="p-5 flex flex-col flex-grow justify-between">
                      <div className="space-y-1.5">
                        <h3 className="font-bold text-stone-900 text-lg group-hover:text-emerald-600 transition-colors">
                          {color.name}
                        </h3>
                      </div>

                      <div className="pt-4 mt-auto">
                        <button
                          onClick={() => {
                            setMessageText(`Jeg er meget interesseret i farven "${color.name}" til mit projekt. Lad os se, hvordan det ser ud!`);
                            const formElement = document.getElementById('vusion-form-section');
                            if (formElement) {
                              formElement.scrollIntoView({ behavior: 'smooth' });
                            }
                          }}
                          className="w-full py-2 bg-stone-50 hover:bg-emerald-50 border border-stone-200 hover:border-emerald-200 text-stone-700 hover:text-emerald-700 rounded-xl text-xs font-bold transition-all duration-200 flex items-center justify-center gap-1.5 cursor-pointer"
                        >
                          <Sparkles className="h-3.5 w-3.5" />
                          Visualiser denne farve
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>

          {/* Info Box */}
          <div className="bg-stone-50 rounded-2xl border border-stone-200 overflow-hidden shadow-sm grid grid-cols-1 lg:grid-cols-12 gap-8 items-center p-6 sm:p-8">
            <div className="lg:col-span-7 relative overflow-hidden rounded-xl aspect-video group">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/6220a5-03b6d8988038445f889647a00d1e7e3f-mv2.jpg"
                alt="Stort udvalg af stenblandinger og farver"
                className="w-full h-full object-cover group-hover:scale-108 transition-transform duration-700 ease-out"
              />
            </div>
            <div className="lg:col-span-5 space-y-4">
              <div className="flex items-center gap-2 text-emerald-600">
                <Info className="h-5 w-5" />
                <span className="text-xs font-bold tracking-widest uppercase">Ubegrænsede Muligheder</span>
              </div>
              <h3 className="text-2xl font-bold text-stone-900">Over 150 forskellige farvemuligheder</h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Ud over vores standardblandinger har du adgang til et gigantisk kartotek af farver og stentyper. Vi kan tilpasse blandingen, så den matcher husets mursten, tag, vinduesrammer eller de omgivende havemure perfekt.
              </p>
              <p className="text-stone-600 text-sm leading-relaxed">
                Det er endda muligt at sammensætte din helt egen, unikke farvekombination, som bliver speciallavet til netop dit projekt.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Designkategorier */}
      <section className="py-16 bg-stone-50 border-t border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-stone-900">Designmuligheder til enhver stil</h2>
            <p className="text-stone-500 mt-2">
              Uanset om du bor i en klassisk patriciervilla, et moderne funkis-hus eller en hyggelig landejendom, har vi en stenblanding, der passer til.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {designCategories.map((cat, idx) => (
              <div key={idx} className="p-6 bg-white rounded-2xl border border-stone-200 shadow-sm space-y-2">
                <h3 className="font-bold text-stone-900 text-base flex items-center gap-2">
                  <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                  {cat.title}
                </h3>
                <p className="text-stone-600 text-xs leading-relaxed">{cat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sektion: Vusion by Vuba (Digital Visualisering) */}
      <section id="vusion-form-section" className="py-16 bg-white border-t border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="flex items-center gap-2 text-emerald-600">
                <Sparkles className="h-5 w-5" />
                <span className="text-xs font-bold tracking-widest uppercase">Digital Visualisering</span>
              </div>
              <h2 className="text-3xl font-bold text-stone-900 leading-tight">
                Send et billede til os og vi forvandler det digitalt med Vusion by Vuba.
              </h2>
              <p className="text-stone-600 leading-relaxed">
                Det kan være svært at forestille sig, hvordan en ny fugefri stenbelægning vil se ud foran netop dit hus. Derfor tilbyder vi en professionel, digital visualisering via det avancerede <strong>Vusion by Vuba</strong> system.
              </p>
              <p className="text-stone-600 leading-relaxed">
                Alt du skal gøre er at tage et godt billede af din nuværende indkørsel, terrasse eller gangsti og sende det til os via formularen. Vores designere vil derefter digitalt lægge forskellige stenbelægninger, farver og designs ind på dit billede. Det gør det nemt og trygt for dig at træffe den helt rigtige beslutning, før arbejdet går i gang.
              </p>

              <div className="space-y-3 pt-2">
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 shrink-0 mt-0.5">
                    <Check className="h-3 w-3 stroke-[3]" />
                  </div>
                  <span className="text-sm text-stone-700"><strong>100% uforpligtende:</strong> Det er helt gratis at få lavet en visualisering.</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 shrink-0 mt-0.5">
                    <Check className="h-3 w-3 stroke-[3]" />
                  </div>
                  <span className="text-sm text-stone-700"><strong>Flere farveforslag:</strong> Vi viser dig typisk 2-3 forskellige stenblandinger.</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 shrink-0 mt-0.5">
                    <Check className="h-3 w-3 stroke-[3]" />
                  </div>
                  <span className="text-sm text-stone-700"><strong>Hurtigt svar:</strong> Du modtager dit visualiserede billede direkte i din indbakke.</span>
                </div>
              </div>
            </div>

            {/* Vusion Formular */}
            <div className="bg-stone-50 p-6 sm:p-8 rounded-2xl border border-stone-200 shadow-md">
              <h3 className="text-xl font-bold text-stone-900 mb-4 flex items-center gap-2">
                <ImageIcon className="h-5 w-5 text-emerald-600" />
                Få en gratis visualisering
              </h3>

              {status === 'success' ? (
                <div className="bg-emerald-50 border border-emerald-200 p-6 rounded-xl text-center space-y-3">
                  <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" strokeWidth={1.5} />
                  <h4 className="font-bold text-emerald-900 text-lg">Tak for din forespørgsel!</h4>
                  <p className="text-sm text-emerald-700">
                    Vi har modtaget dit billede og dine oplysninger. Vores designere går i gang med din digitale visualisering, og du vil høre fra os hurtigst muligt.
                  </p>
                  <button
                    onClick={() => {
                      setStatus('idle');
                      setMessageText('');
                    }}
                    className="mt-4 px-4 py-2 bg-emerald-600 text-white rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-emerald-700 transition-colors cursor-pointer"
                  >
                    Send endnu et billede
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Honeypot field for anti-spam */}
                  <input
                    type="text"
                    name="website_url"
                    className="sr-only absolute"
                    tabIndex={-1}
                    autoComplete="off"
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-1">Navn *</label>
                      <input
                        type="text"
                        name="navn"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-stone-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                        placeholder="Dit fulde navn"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-1">Telefon *</label>
                      <input
                        type="tel"
                        name="telefon"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-stone-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                        placeholder="Dit telefonnummer"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-1">E-mail *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-stone-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                      placeholder="din@email.dk"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-1">Besked / Ønsker til farve</label>
                    <textarea
                      name="besked"
                      rows="3"
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-lg border border-stone-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                      placeholder="Fortæl os lidt om dit projekt og hvilke farver du overvejer (f.eks. lyse, grå eller varme sandfarver)..."
                    ></textarea>
                  </div>

                  {/* File Upload Area */}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-1">Upload billede af dit område *</label>
                    <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-stone-200 border-dashed bg-white rounded-xl hover:border-emerald-500 transition-colors cursor-pointer relative">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        multiple
                        accept=".png,.jpg,.jpeg,.webp"
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                      <div className="space-y-1 text-center">
                        <Upload className="mx-auto h-10 w-10 text-stone-400" />
                        <div className="flex text-sm text-stone-600 justify-center">
                          <span className="relative font-semibold text-emerald-600 hover:text-emerald-700">Klik for at uploade</span>
                          <p className="pl-1">eller træk og slip</p>
                        </div>
                        <p className="text-xs text-stone-400">PNG, JPG, WEBP op til 10 MB pr. billede (maks 5 billeder)</p>
                      </div>
                    </div>

                    {/* Selected Files Preview */}
                    {selectedFiles.length > 0 && (
                      <div className="mt-3 space-y-2">
                        <p className="text-xs font-bold uppercase tracking-wider text-stone-700">Valgte billeder ({selectedFiles.length}):</p>
                        <div className="grid grid-cols-1 gap-2">
                          {selectedFiles.map((file, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-white rounded-lg border border-stone-200 text-xs text-stone-700">
                              <span className="truncate max-w-[200px] font-medium">{file.name}</span>
                              <div className="flex items-center gap-2">
                                <span>{(file.size / (1024 * 1024)).toFixed(2)} MB</span>
                                <button
                                  type="button"
                                  onClick={() => removeFile(idx)}
                                  className="p-1 text-stone-400 hover:text-red-500 rounded-full hover:bg-stone-100"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {status === 'error' && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2.5 text-xs text-red-700">
                      <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                      <span>{errorMessage}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'submitting' || selectedFiles.length === 0}
                    className="w-full flex items-center justify-center px-4 py-3 rounded-xl text-sm font-bold uppercase tracking-wider text-white bg-stone-900 hover:bg-stone-800 disabled:bg-stone-300 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    {status === 'submitting' ? (
                      <>
                        <Loader2 className="animate-spin h-4 w-4 mr-2" />
                        Sender billeder...
                      </>
                    ) : (
                      'Send billeder og få visualisering'
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-stone-900 text-white py-16 w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold">Klar til at vælge din nye belægning?</h2>
          <p className="text-stone-400 mt-4 max-w-2xl mx-auto">
            Kontakt os i dag for at få tilsendt fysiske stenprøver, eller få et uforpligtende tilbud på din nye, eksklusive belægning.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center px-6 py-3 rounded-xl font-bold bg-emerald-600 text-white hover:bg-emerald-700 hover:-translate-y-1 transition-all duration-300"
            >
              Få et tilbud & stenprøver
            </Link>
            <Link
              to="/referencer"
              className="inline-flex items-center justify-center px-6 py-3 rounded-xl font-bold bg-stone-800 hover:bg-stone-700 border border-stone-700 hover:-translate-y-1 transition-all duration-300"
            >
              Se vore referencer
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
